import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { applicationApi, integrationApi, jobApi, jobPortalApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { StatStrip, type StatSegment } from '@/components/ui/StatStrip'
import { useApplyDialog } from '@/features/applications/ApplyOptionsDialog'
import { refreshLicense } from '@/features/billing/useLicense'
import { AddJobModal } from '@/features/jobs/AddJobModal'
import { JobCard } from '@/features/jobs/JobCard'
import { JobFilterBar, countJobFilters, type JobFilters } from '@/features/jobs/JobFilterBar'
import { JobPreferencesModal } from '@/features/jobs/JobPreferencesModal'
import { browseJobsHref, sourceFilterFor, sourceOptionId } from '@/features/jobs/sourceFilter'
import {
  ApplicationsTable,
  Pager,
  StatusTabs,
  statusesFor,
} from '@/features/applications/ApplicationsTable'
import { cn, relativeTime } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'
import { isLicenseError } from '@/store/license'
import type { ApplicationStats, ApplyOptions, JobMatch, JobSourceOption } from '@/types/api'
import { fromCurrent } from '@/features/applications/backTarget'
import { ReviewInboxPanel } from '@/features/applications/ReviewInboxPage'

/** Match cards on the board at once. */
const VISIBLE = 5
/** The narrowest a match card may get, and the gap between them (keep in step with CAROUSEL). */
const CARD_MIN = 240
const CARD_GAP = 16

/**
 * How many match cards to show so every row is full: as many as fit across
 * the board (whatever its width is right now: wide screen, docked assistant,
 * tablet), or two rows of two when only two fit. On a phone the board is a
 * swipeable row and shows the usual five.
 */
function useFullRows(ref: React.RefObject<HTMLElement>): number {
  const [count, setCount] = useState(VISIBLE)
  useEffect(() => {
    const node = ref.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      if (window.matchMedia('(max-width: 639px)').matches) return setCount(VISIBLE)
      const columns = Math.max(1, Math.floor((node.clientWidth + CARD_GAP) / (CARD_MIN + CARD_GAP)))
      setCount(columns >= 3 ? Math.min(columns, 6) : columns === 2 ? 4 : 3)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref])
  return count
}
/** Matches kept loaded; the rest wait in reserve behind the board. */
const MATCH_BUFFER = 20

const FILTERS_KEY = 'dashboard-job-filters'

/** The filter bar's choices, kept in this browser between visits. */
function useSavedFilters(): [JobFilters, (next: JobFilters) => void] {
  const [filters, setFilters] = useState<JobFilters>(() => {
    try {
      const saved = localStorage.getItem(FILTERS_KEY)
      return saved ? (JSON.parse(saved) as JobFilters) : {}
    } catch {
      return {}
    }
  })
  const save = (next: JobFilters) => {
    setFilters(next)
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(next))
    } catch {
      /* private mode: the filters last for this visit */
    }
  }
  return [filters, save]
}

export function DashboardPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const applicationsRef = useRef<HTMLElement>(null)
  const boardRef = useRef<HTMLElement>(null)
  const visible = useFullRows(boardRef)

  const [source, setSource] = useState<string>('all')
  const [filters, setFilters] = useSavedFilters()
  const filtered = countJobFilters(filters) + (filters.q ? 1 : 0) > 0
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [addOpen, setAddOpen] = useState(false)
  const [findOpen, setFindOpen] = useState(false)
  // Cards taken off the board by Pass / Apply before the server confirms, so
  // the next match slides in at once instead of after a round trip.
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set())
  const hide = (jobIds: string[]) => setRemoved((current) => new Set([...current, ...jobIds]))
  const unhide = (jobIds: string[]) =>
    setRemoved((current) => new Set([...current].filter((id) => !jobIds.includes(id))))

  const careerPathsQuery = useQuery({ queryKey: ['career-paths'], queryFn: jobApi.careerPaths, staleTime: 300_000, retry: false })
  const [showCareerPaths, setShowCareerPaths] = useState(false)
  const exploreRole = useMutation({
    mutationFn: (title: string) => jobApi.autoApplySearch({ filters: { q: title, locations: filters.locations, sources: filters.sources, boards: filters.boards, workplaceTypes: filters.workplaceTypes?.filter((value): value is 'remote' | 'hybrid' | 'onsite' => ['remote', 'hybrid', 'onsite'].includes(value)), employmentTypes: filters.employmentTypes, maxExperienceYears: filters.maxExperienceYears }, page: 1, pageSize: 25, refresh: true }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['top-matches'] }) },
    onError: (error: ApiError) => toast.info('Showing existing matches', error.message),
  })

  const sourcesQuery = useQuery({
    queryKey: ['job-sources'],
    queryFn: jobApi.sources,
    staleTime: 30_000,
    // While the first career-page match runs, ask again until it lands.
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.source === 'internal' && s.matching) ? 5000 : false,
  })
  const careersMatching = Boolean(
    sourcesQuery.data?.some((s) => s.source === 'internal' && s.matching),
  )
  const careersNote = sourcesQuery.data?.find((s) => s.source === 'internal')?.note ?? null
  // The match just finished: show its jobs.
  const wasMatching = useRef(false)
  useEffect(() => {
    if (wasMatching.current && !careersMatching) {
      queryClient.invalidateQueries({ queryKey: ['top-matches'] })
    }
    wasMatching.current = careersMatching
  }, [careersMatching, queryClient])

  const matchesQuery = useQuery({
    queryKey: ['top-matches', source, filters],
    // The five shown plus fifteen in reserve (the API's maximum), so Pass,
    // Apply and "Apply to all" swap in the next card without a refetch.
    queryFn: () => {
      const { sources, boards } = sourceFilterFor(source)
      return jobApi.topMatches(MATCH_BUFFER, 0, sources, boards, filters)
    },
    staleTime: 30_000,
    // Keep the board while new filters load, instead of flashing skeletons.
    placeholderData: (previous) => previous,
  })

  const statsQuery = useQuery({
    queryKey: ['application-stats'],
    queryFn: applicationApi.stats,
    staleTime: 20_000,
    refetchInterval: (query) =>
      (query.state.data?.queued ?? 0) + (query.state.data?.inFlight ?? 0) > 0 ? 3000 : false,
  })
  const working = (statsQuery.data?.queued ?? 0) + (statsQuery.data?.inFlight ?? 0) > 0

  const applicationsQuery = useQuery({
    queryKey: ['applications', statusFilter, search, page, pageSize],
    queryFn: () =>
      applicationApi.list({
        page,
        pageSize,
        statuses: statusesFor(statusFilter),
        search: search.trim() || undefined,
      }),
    staleTime: 15_000,
    // While the worker is applying, keep the table live without a reload.
    refetchInterval: working ? 3000 : false,
  })

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['top-matches'] })
    queryClient.invalidateQueries({ queryKey: ['job-sources'] })
    queryClient.invalidateQueries({ queryKey: ['applications'] })
    queryClient.invalidateQueries({ queryKey: ['application-stats'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
  }

  const sync = useMutation({
    mutationFn: () => integrationApi.sync(50),
    onSuccess: (result) => {
      refreshAll()
      toast.success(
        `Pulled ${result.jobsFound} job${result.jobsFound === 1 ? '' : 's'} from Naukri`,
        result.jobsImported ? `${result.jobsImported} new` : 'Nothing new since the last sync',
      )
    },
    onError: (error: ApiError) => toast.error('Could not sync Naukri', error.message),
  })

  const applyDialog = useApplyDialog()

  // Cards leave the board when the dialog is confirmed, not when it opens.
  const apply = useMutation({
    mutationFn: ({ jobId, options }: { jobId: string; options: ApplyOptions }) =>
      applicationApi.create({ jobId, ...options }),
    onMutate: ({ jobId }) => hide([jobId]),
    onSuccess: (application) => {
      refreshAll()
      refreshLicense(queryClient)
      toast.success('Applying', `${application.job.companyName} — we'll flag anything that needs you`)
    },
    onError: (error: ApiError, { jobId }) => {
      unhide([jobId])
      if (isLicenseError(error)) return
      toast.error('Could not apply', error.message)
    },
  })

  const pass = useMutation({
    mutationFn: (jobId: string) => jobApi.pass(jobId),
    onMutate: (jobId) => hide([jobId]),
    // Refill the buffer in the background; the board already moved on.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['top-matches'] }),
    onError: (error: ApiError, jobId) => {
      unhide([jobId])
      toast.error('Could not pass', error.message)
    },
  })

  const bulkApply = useMutation({
    mutationFn: ({ jobIds, options }: { jobIds: string[]; options: ApplyOptions }) =>
      applicationApi.bulkApply(jobIds, options),
    onMutate: ({ jobIds }) => hide(jobIds),
    onSuccess: (result) => {
      // Over the daily limit means not applied: put those back on the board.
      unhide(result.skipped.filter((s) => s.reason === 'daily_limit_reached').map((s) => s.jobId))
      refreshAll()
      refreshLicense(queryClient)
      const skipped = result.skipped.length
      toast.success(
        `Applying to ${result.queued.length} job${result.queued.length === 1 ? '' : 's'}`,
        skipped ? `${skipped} skipped — already applied, closed, or over today's limit.` : undefined,
      )
    },
    onError: (error: ApiError, { jobIds }) => {
      unhide(jobIds)
      if (isLicenseError(error)) return
      toast.error('Bulk apply failed', error.message)
    },
  })

  const matches = useMemo(
    () => (matchesQuery.data ?? []).filter((m) => !removed.has(m.job.id)).slice(0, visible),
    [matchesQuery.data, removed, visible],
  )
  const applicable = useMemo(() => matches.filter((m) => !m.hasApplied), [matches])
  const naukri = sourcesQuery.data?.find((s) => s.source === 'naukri' && !s.board)

  const handleApply = (match: JobMatch) =>
    applyDialog.ask([match.job], (options) => apply.mutate({ jobId: match.job.id, options }))
  const handleApplyAll = () => {
    const targets = applicable.map((m) => m.job)
    applyDialog.ask(targets, (options) =>
      bulkApply.mutate({ jobIds: targets.map((job) => job.id), options }),
    )
  }
  const handlePass = (match: JobMatch) => pass.mutate(match.job.id)
  const changeStatus = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }
  /** Filter the list below and bring it into view (from the strip or the attention block). */
  const jumpToStatus = (value: string) => {
    changeStatus(value)
    applicationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const stats = statsQuery.data
  const firstName = user?.fullName?.trim().split(/\s+/)[0]
  const needsYou = stats?.needsYou ?? 0
  const summary = [
    matches.length > 0 && `${applicable.length} new match${applicable.length === 1 ? '' : 'es'} ready`,
    needsYou > 0 && `${needsYou} application${needsYou === 1 ? '' : 's'} waiting on you`,
    working && 'applying in the background',
  ].filter(Boolean)
  const viewAllLabel =
    source === 'all' ? '' : sourcesQuery.data?.find((s) => sourceOptionId(s) === source)?.label ?? ''

  return (
    <div className="space-y-7 sm:space-y-9">
      <div className="space-y-4">
        <header className="rounded-card border border-brand/20 bg-gradient-to-br from-brand-soft/70 via-surface to-surface p-5 sm:p-6">
          <p className="label-eyebrow mb-2 text-brand">Home · Your job search</p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[24px]">
              {greeting()}
              {firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="label-eyebrow">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <p className="mt-1 text-[13.5px] text-ink-muted sm:text-[14px]">
            {summary.length > 0
              ? capitalise(summary.join(' · '))
              : 'Find your next role, review applications, and follow your progress.'}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate('/dashboard/review')}>Needs review{needsYou > 0 ? ` (${needsYou})` : ''}</Button>
            <Button size="sm" variant="secondary" onClick={() => navigate('/dashboard/tracker?tab=applications')}>Track applications</Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/dashboard/tracker?tab=inbox')}>Employer inbox</Button>
          </div>
        </header>

        <StatStrip
          segments={statSegments(stats)}
          loading={!stats}
          active={statusFilter}
          onSelect={(key) => jumpToStatus(statusFilter === key ? 'all' : key)}
        />

        <Attention stats={stats} onShowNeedsYou={() => jumpToStatus('needs_you')} />
      </div>

      {Boolean(careerPathsQuery.data?.length) && (
        <section className="card p-5" aria-label="Related career directions">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h2 className="text-[17px] font-semibold text-ink">Explore related roles</h2><p className="mt-1 text-[13px] text-ink-muted">Based on your skills and experience. Each opening is still scored against its own requirements.{exploreRole.isPending ? ' Looking for current openings…' : ''}</p></div>
            <button type="button" className="text-[13px] font-medium text-brand" onClick={() => setShowCareerPaths((v) => !v)}>{showCareerPaths ? 'Show fewer' : 'Show all'}</button>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(showCareerPaths ? careerPathsQuery.data! : careerPathsQuery.data!.slice(0, 6)).map((path) => (
              <button type="button" key={path.title} disabled={exploreRole.isPending} onClick={() => { setFilters({ ...filters, q: path.title }); exploreRole.mutate(path.title); boardRef.current?.scrollIntoView({ behavior: 'smooth' }) }} className="rounded-field border border-line p-3 text-left transition hover:border-brand/40 hover:bg-brand-soft/30">
                <span className="text-[14px] font-semibold text-ink">{path.title}</span>
                <span className="mt-1 block text-[12px] leading-relaxed text-ink-muted">{path.reason}</span>
                {path.needsConfirmation && <span className="mt-2 block text-[11px] font-medium text-warning">Confirm experience before choosing this direction</span>}
              </button>
            ))}
          </div>
        </section>
      )}

      <section ref={boardRef} aria-labelledby="top-matches-heading">
        <div className="mb-5">
          <JobFilterBar value={filters} onChange={setFilters} searching={matchesQuery.isFetching && filtered} />
        </div>
        <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0">
            <h2 id="top-matches-heading" className="text-[18px] font-semibold tracking-tight text-ink">
              Top job matches
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              {filtered
                ? 'Your best matches within these filters. Apply to all uses them too.'
                : 'Ranked by how well they fit your profile.'}
            </p>
          </div>

          {/* Phone: the primary action full width, the rest in one scrolling row. Desktop: one row. */}
          <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-end">
            <div className="hscroll order-2 -mx-4 items-center gap-2 px-4 md:order-1 md:mx-0 md:flex-wrap md:justify-end md:overflow-visible md:px-0">
              <Button variant="secondary" size="sm" className="shrink-0" icon={<PlusIcon />} onClick={() => setAddOpen(true)}>
                Add your own
              </Button>
              <Button variant="secondary" size="sm" className="shrink-0" icon={<TargetIcon />} onClick={() => setFindOpen(true)}>
                Find jobs for me
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                icon={<BrowseIcon />}
                onClick={() => navigate(browseJobsHref(source))}
              >
                View all {viewAllLabel}
              </Button>
            </div>
            <Button
              size="sm"
              className="order-1 h-11 w-full whitespace-nowrap md:order-2 md:h-9 md:w-auto"
              icon={<SparkIcon />}
              onClick={handleApplyAll}
              disabled={applicable.length === 0}
              loading={bulkApply.isPending}
            >
              Apply to all <span className="num">{applicable.length}</span>
            </Button>
          </div>
        </div>

        <SourceBar
          sources={sourcesQuery.data ?? []}
          active={source}
          onSelect={setSource}
          onSync={() => sync.mutate()}
          syncing={sync.isPending}
          onConnect={() => navigate('/dashboard/settings')}
        />

        {matchesQuery.isLoading ? (
          <div className={CAROUSEL}>
            {Array.from({ length: visible }).map((_, index) => (
              <div key={index} className={CAROUSEL_ITEM}>
                <Skeleton className="h-[270px] rounded-card" />
              </div>
            ))}
          </div>
        ) : matchesQuery.isError ? (
          <div className="card">
            <ErrorState
              description="We could not load your matches."
              onRetry={() => matchesQuery.refetch()}
            />
          </div>
        ) : matches.length === 0 && filtered ? (
          <div className="card">
            <EmptyState
              title="No matches within these filters"
              description="Widen a filter or clear them to see every match."
              action={
                <Button variant="secondary" onClick={() => setFilters({})}>
                  Clear filters
                </Button>
              }
            />
          </div>
        ) : matches.length === 0 && careersMatching ? (
          <div className="card">
            <EmptyState
              title="Matching your profile with company career pages"
              description="We are checking the jobs crawled from company career sites against your profile. Your best matches show up here in a minute or two. You don't need to connect a portal for this."
            />
          </div>
        ) : matches.length === 0 && careersNote ? (
          <div className="card">
            <EmptyState
              title="Tell us what to look for"
              description={careersNote}
              action={
                <Button onClick={() => setFindOpen(true)}>Find jobs for me</Button>
              }
            />
          </div>
        ) : matches.length === 0 ? (
          <div className="card">
            <EmptyState
              title={naukri?.connected ? 'No new matches' : 'No matches yet'}
              description={
                naukri?.connected
                  ? 'Find jobs on company career pages, pull the latest from Naukri, or add a job link and we will score it.'
                  : 'Tell us what you want and we will find matching jobs on company career pages. Connect Naukri to add the jobs it recommends for you.'
              }
              action={
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="secondary" onClick={() => setAddOpen(true)}>
                    Add a job link
                  </Button>
                  <Button variant="secondary" onClick={() => setFindOpen(true)}>
                    Find jobs for me
                  </Button>
                  {naukri?.connected ? (
                    <Button onClick={() => sync.mutate()} loading={sync.isPending}>
                      Sync Naukri
                    </Button>
                  ) : (
                    <Button onClick={() => navigate('/dashboard/settings')}>Connect Naukri</Button>
                  )}
                </div>
              }
            />
          </div>
        ) : (
          <div className={CAROUSEL} aria-label="Top job matches">
            {matches.map((match) => (
              <div key={match.id} className={CAROUSEL_ITEM}>
                <JobCard match={match} onApply={handleApply} onPass={handlePass} />
              </div>
            ))}
          </div>
        )}
      </section>

      {(stats?.needsYou ?? 0) > 0 && (
        <section aria-label="Questions waiting on you" className="scroll-mt-20">
          <ReviewInboxPanel embedded />
        </section>
      )}

      <section ref={applicationsRef} className="card scroll-mt-20 overflow-hidden">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-3 px-4 pb-3 pt-4 sm:flex sm:px-5">
          <h2 className="text-[18px] font-semibold tracking-tight text-ink sm:flex-1">All applications</h2>
          {(stats?.needsYou ?? 0) > 0 && (
            <Button size="sm" className="sm:order-last" onClick={() => navigate('/dashboard/review')}>
              Answer all {stats!.needsYou} in one place
            </Button>
          )}
          <Button variant="ghost" size="sm" className="sm:order-last" onClick={() => navigate('/dashboard/tracker')}>
            Open tracker
          </Button>
          <div className="relative col-span-2 min-w-0 sm:col-span-1 sm:w-[240px]">
            <label className="sr-only" htmlFor="app-search">
              Search applications
            </label>
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
              <BrowseIcon />
            </span>
            <input
              id="app-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              placeholder="Search company…"
              className="field-box h-11 w-full py-1.5 pl-9 text-[14px] sm:h-9"
            />
          </div>
        </header>

        <div className="border-b border-line px-3 pb-3 sm:px-4">
          <StatusTabs active={statusFilter} stats={stats} onChange={changeStatus} />
        </div>

        <ApplicationsTable
          applications={applicationsQuery.data?.data ?? []}
          loading={applicationsQuery.isLoading}
          emptyTitle={
            statusFilter === 'all' && !search.trim() ? 'No applications yet' : 'Nothing matches this view'
          }
          emptyDescription={
            statusFilter === 'all' && !search.trim()
              ? 'Apply to one of your top matches and it will show up here, with live status.'
              : 'Try another status or clear the search.'
          }
          emptyAction={
            statusFilter === 'all' && !search.trim() ? (
              <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard/jobs')}>
                Browse jobs
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearch('')
                  changeStatus('all')
                }}
              >
                Show all applications
              </Button>
            )
          }
          onOpen={(application) =>
            navigate(
              `/dashboard/applications/${application.id}${
                statusFilter === 'all' ? '' : `?status=${statusFilter}`
              }`,
              // Its back arrow returns here, to Today.
              { state: fromCurrent() },
            )
          }
        />
        {(applicationsQuery.data?.pageInfo.totalItems ?? 0) > 0 && (
          <Pager
            page={page}
            pageSize={pageSize}
            totalItems={applicationsQuery.data?.pageInfo.totalItems ?? 0}
            onPage={setPage}
            onPageSize={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}
      </section>

      {applyDialog.element}
      <AddJobModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={refreshAll} />
      <JobPreferencesModal
        open={findOpen}
        onClose={() => setFindOpen(false)}
        onFound={() => {
          // Show the new matches whichever source chip was active.
          setRemoved(new Set())
          refreshAll()
        }}
      />
    </div>
  )
}

/** Mobile: a swipeable row of ~80vw cards. From sm up: a regular grid. */
// From `sm` the grid fits as many 240px+ cards as the *available* width allows,
// so it is five columns on a wide screen and two beside the docked assistant.
const CAROUSEL =
  'hscroll -mx-4 snap-x snap-mandatory scroll-px-4 gap-3 px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0'
const CAROUSEL_ITEM = 'w-[80vw] max-w-[340px] shrink-0 snap-start sm:w-auto sm:max-w-none'

/** The four counts in the strip; keys are the status tabs they filter to. */
function statSegments(stats: ApplicationStats | undefined): StatSegment[] {
  const applying = (stats?.inFlight ?? 0) + (stats?.queued ?? 0)
  const needsYou = stats?.needsYou ?? 0
  const failed = stats?.failed ?? 0
  return [
    { key: 'submitted', label: 'Submitted', value: stats?.submitted ?? 0, tone: 'success' },
    { key: 'in_flight', label: 'Applying', value: applying, tone: 'info', live: applying > 0 },
    { key: 'needs_you', label: 'Needs you', value: needsYou, tone: needsYou > 0 ? 'warning' : 'neutral' },
    { key: 'failed', label: 'Failed', value: failed, tone: failed > 0 ? 'danger' : 'neutral' },
  ]
}

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 5) return 'Good evening'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// ---------------------------------------------------------------------------
// Attention: only what needs the person now
// ---------------------------------------------------------------------------

/**
 * Applications waiting on an answer, and the portals nudge while no portal is
 * picked. When there is nothing, one quiet line says so.
 */
function Attention({
  stats,
  onShowNeedsYou,
}: {
  stats: ApplicationStats | undefined
  onShowNeedsYou: () => void
}) {
  const nudge = usePortalsNudge()
  const needsYou = stats?.needsYou ?? 0

  if (!stats || nudge.pending) return null

  if (needsYou === 0 && !nudge.show) {
    return (
      <p className="flex items-center gap-2 px-1 text-[13px] text-ink-muted" role="status">
        <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
        All clear. Nothing needs you right now.
      </p>
    )
  }

  return (
    <section aria-label="Needs your attention" className="panel divide-y divide-line overflow-hidden">
      {needsYou > 0 && (
        <AttentionRow
          tone="warning"
          title={`${needsYou} application${needsYou === 1 ? '' : 's'} need${needsYou === 1 ? 's' : ''} you`}
          description="Answer missing questions in Needs review, then queue every ready application."
          onClick={onShowNeedsYou}
          trailing={
            <Link
              to="/dashboard/review"
              className="inline-flex h-9 items-center rounded-field px-2.5 text-[13px] font-medium text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink"
            >
              Needs review
            </Link>
          }
        />
      )}
      {nudge.show && (
        <AttentionRow
          tone="info"
          title="Choose your job portals"
          description="Pick the job boards we should search and apply on for you."
          to="/dashboard/profile/portals"
          trailing={
            <button
              type="button"
              onClick={nudge.dismiss}
              aria-label="Dismiss"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-field text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
            >
              <CloseIcon />
            </button>
          }
        />
      )}
    </section>
  )
}

const ATTENTION_DOT: Record<'warning' | 'info', string> = {
  warning: 'bg-warning',
  info: 'bg-info',
}

/** One row: a dot, the message, the action; the whole text is the tap target. */
function AttentionRow({
  tone,
  title,
  description,
  onClick,
  to,
  trailing,
}: {
  tone: 'warning' | 'info'
  title: string
  description: string
  onClick?: () => void
  to?: string
  trailing?: ReactNode
}) {
  const body = (
    <>
      <span className={cn('mt-[7px] h-2 w-2 shrink-0 rounded-full', ATTENTION_DOT[tone])} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold text-ink">{title}</span>
        <span className="block text-[13px] leading-snug text-ink-muted">{description}</span>
      </span>
      <ChevronIcon />
    </>
  )
  const className =
    'flex min-h-[56px] min-w-0 flex-1 items-start gap-3 py-3 pl-4 text-left transition-colors hover:bg-surface-muted/60 sm:pl-5'

  return (
    <div className="flex items-center pr-2">
      {to ? (
        <Link to={to} className={className}>
          {body}
        </Link>
      ) : (
        <button type="button" onClick={onClick} className={className}>
          {body}
        </button>
      )}
      {trailing}
    </div>
  )
}

const PORTALS_NUDGE_KEY = 'portals-nudge-dismissed'

/**
 * Whether to point at Profile → Job Portals: only for someone who has not
 * picked any yet. Purely a hint: no portals endpoint, or an error from it,
 * shows nothing.
 */
function usePortalsNudge() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(PORTALS_NUDGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const portalsQuery = useQuery({
    queryKey: ['job-portals'],
    queryFn: jobPortalApi.list,
    enabled: !dismissed,
    retry: false,
    staleTime: 60_000,
  })

  const portals = portalsQuery.data
  const show =
    !dismissed && Array.isArray(portals) && !portals.some((portal) => portal.selected)

  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(PORTALS_NUDGE_KEY, '1')
    } catch {
      /* private mode — the row simply returns next time */
    }
  }

  return { show, pending: !dismissed && portalsQuery.isPending, dismiss }
}

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

/** "Pull jobs from" — which boards feed the matches, and applying happens back on them. */
function SourceBar({
  sources,
  active,
  onSelect,
  onSync,
  syncing,
  onConnect,
}: {
  sources: JobSourceOption[]
  active: string
  onSelect: (source: string) => void
  onSync: () => void
  syncing: boolean
  onConnect: () => void
}) {
  const naukri = sources.find((s) => s.source === 'naukri' && !s.board)
  const total = sources.reduce((sum, s) => sum + s.jobCount, 0)

  return (
    <div className="mb-4 flex flex-col gap-2 border-y border-line py-2.5 md:flex-row md:items-center md:justify-between">
      <div className="hscroll -mx-4 items-center gap-1.5 px-4 md:mx-0 md:px-0">
        <span className="mr-1 shrink-0 text-[12.5px] text-ink-muted">Pull jobs from</span>
        <Chip selected={active === 'all'} onClick={() => onSelect('all')}>
          All <Count>{total}</Count>
        </Chip>
        {sources.map((option) => (
          <Chip
            key={sourceOptionId(option)}
            selected={active === sourceOptionId(option)}
            disabled={!option.available}
            onClick={() => onSelect(sourceOptionId(option))}
            title={
              !option.available
                ? 'Coming soon'
                : option.source === 'naukri' && !option.connected
                  ? 'Connect Naukri in Settings'
                  : undefined
            }
          >
            {option.connected && (option.board || option.source !== 'manual') && (
              <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
            )}
            {option.label}
            {option.available ? <Count>{option.jobCount}</Count> : <Count>soon</Count>}
          </Chip>
        ))}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 text-[12px] text-ink-muted md:justify-end">
        {naukri?.connected ? (
          <>
            <span className="inline-flex items-center gap-1.5">
              <ClockIcon />
              Synced {naukri.lastSyncedAt ? relativeTime(naukri.lastSyncedAt) : 'never'}
            </span>
            <Button variant="ghost" size="sm" onClick={onSync} loading={syncing}>
              Sync now
            </Button>
          </>
        ) : (
          <Button size="sm" className="w-full md:w-auto" onClick={onConnect}>
            Connect Naukri
          </Button>
        )}
      </div>
    </div>
  )
}

function Chip({
  selected,
  disabled,
  onClick,
  title,
  children,
}: {
  selected: boolean
  disabled?: boolean
  onClick: () => void
  title?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={selected}
      className={cn(
        'tap inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill border px-2.5 text-[13px] font-medium transition-colors',
        selected
          ? 'border-ink bg-ink text-oncolor'
          : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
        disabled && 'cursor-not-allowed opacity-50 hover:border-line hover:text-ink-soft',
      )}
    >
      {children}
    </button>
  )
}

function Count({ children }: { children: ReactNode }) {
  return <span className="num text-[11.5px] opacity-60">{children}</span>
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ClockIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 4.3V7l1.8 1.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function BrowseIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m9.3 9.3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function TargetIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="7" cy="7" r="1.6" fill="currentColor" />
    </svg>
  )
}

function SparkIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 1.5 8.3 5.2 12 6.5 8.3 7.8 7 11.5 5.7 7.8 2 6.5l3.7-1.3L7 1.5Z" fill="currentColor" />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg className="mt-[9px] h-3.5 w-3.5 shrink-0 text-ink-faint" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="m5 3.5 3.5 3.5L5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
